// DatabaseSettingsAboutTab.jsx

import React from 'react';

// CSS
import './DatabaseSettingsAboutTab.css';

// Components
import DatabaseAboutEditorForm from './DatabaseAboutEditorForm';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';

/**
 * DatabaseSettingsAboutTab
 *
 * Renders the about tab for editing database metadata and top-level actions.
 *
 * @param {string} iconKey - Current database icon key.
 * @param {function} handleIconKeyChange - Updates the database icon key.
 * @param {boolean} saving - Whether a metadata save is in progress.
 * @param {boolean} deleting - Whether a delete flow is in progress.
 * @param {boolean} canSave - Whether metadata can currently be saved.
 * @param {string} label - Current singular database label.
 * @param {function} setLabel - Updates the singular database label.
 * @param {string} saveError - Current metadata save error.
 * @param {object} duplicateState - Duplicate label validation state.
 * @param {function} handleSave - Persists the current metadata draft.
 * @param {string} labelPlural - Current plural database label.
 * @param {function} handleLabelPluralChange - Updates the plural database label.
 * @param {string} description - Current database description.
 * @param {function} setDescription - Updates the database description.
 * @param {string} defaultView - Current default view key.
 * @param {function} setDefaultView - Updates the default view key.
 * @param {boolean} showPeekOnOpen - Whether opening a node shows Peek first.
 * @param {function} setShowPeekOnOpen - Updates the show-peek-on-open setting.
 * @param {boolean} allowTopLevelCreate - Whether this type can be created from the space.
 * @param {function} setAllowTopLevelCreate - Updates the top-level create setting.
 * @param {boolean} showInMenu - Whether this type appears in the space Menu.
 * @param {function} setShowInMenu - Updates the Menu visibility setting.
 * @param {boolean} showInAddMenu - Whether this type appears in + Add.
 * @param {function} setShowInAddMenu - Updates the Add menu visibility setting.
 * @param {function} [onOpenImport] - Opens the import flow.
 * @param {function} [onExport] - Starts the export flow.
 * @param {boolean} canPublishSchema - Whether schema publishing is available.
 * @param {function} [onPublishSchema] - Publishes the current schema.
 * @param {boolean} publishingSchema - Whether schema publishing is in progress.
 * @param {boolean} canDelete - Whether the delete action is enabled.
 * @param {function} handleOpenDeleteConfirm - Opens the delete confirmation flow.
 *
 * @returns {ReactNode} The rendered about tab content.
 */
const DatabaseSettingsAboutTab = ({
  iconKey,
  handleIconKeyChange,
  saving,
  deleting,
  canSave,
  label,
  setLabel,
  saveError,
  duplicateState,
  handleSave,
  labelPlural,
  handleLabelPluralChange,
  description,
  setDescription,
  defaultView,
  setDefaultView,
  showPeekOnOpen,
  setShowPeekOnOpen,
  allowTopLevelCreate,
  setAllowTopLevelCreate,
  showInMenu,
  setShowInMenu,
  showInAddMenu,
  setShowInAddMenu,
  appearAs,
  setAppearAs,
  schemaFields = [],
  hasOverview = false,
  onOpenImport,
  onExport,
  canPublishSchema,
  onPublishSchema,
  publishingSchema,
  canDelete,
  handleOpenDeleteConfirm,
  allowImport = true,
}) => {

  // ----- RENDER ----- //

  return (
    <div className="database-modal-panel">
      <DatabaseAboutEditorForm
        iconKey={iconKey}
        setIconKey={handleIconKeyChange}
        isSavingType={saving || deleting}
        label={label}
        setLabel={setLabel}
        labelHelperText={saveError || duplicateState.labelError || ''}
        labelPlural={labelPlural}
        pluralHelperText={duplicateState.pluralError || ''}
        description={description}
        setDescription={setDescription}
        defaultView={defaultView}
        setDefaultView={setDefaultView}
        showPeekOnOpen={showPeekOnOpen}
        setShowPeekOnOpen={setShowPeekOnOpen}
        allowTopLevelCreate={allowTopLevelCreate}
        setAllowTopLevelCreate={setAllowTopLevelCreate}
        showInMenu={showInMenu}
        setShowInMenu={setShowInMenu}
        showInAddMenu={showInAddMenu}
        setShowInAddMenu={setShowInAddMenu}
        appearAs={appearAs}
        setAppearAs={setAppearAs}
        schemaFields={schemaFields}
        hasOverview={hasOverview}
        autoFocusLabel={false}
        onLabelPluralChange={handleLabelPluralChange}
      />

      {/* ACTIONS */}
      <div className="database-modal-actions">

        <ModalButtons
          style={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >

          {/* WRAPPER */}
          <div className="database-modal-actions-wrapper">

            {/* ACTIONS LEFT */}
            <div className="database-modal-actions-left">

              <Button
                iconKey="delete"
                label={deleting ? 'Deleting...' : 'Delete'}
                enabled={canDelete}
                onClick={handleOpenDeleteConfirm}
              />
              {allowImport && typeof onOpenImport === 'function' ? (
                <Button
                  iconKey="download"
                  label="Import"
                  onClick={onOpenImport}
                />
              ) : null}
              <Button
                iconKey="upload"
                label="Export"
                onClick={onExport}
              />
              {/* Store v1: app-only publish replaces single-type library publish.
                  Kept commented for possible reuse during _library transition.
              {canPublishSchema ? (
                <Button
                  iconKey="book"
                  enabled={!publishingSchema}
                  title={publishingSchema ? 'Publishing schema...' : 'Publish Schema'}
                  onClick={onPublishSchema}
                />
              ) : null}
              */}
            </div>

            {/* ACTIONS RIGHT */}
            <div className="database-modal-actions-right">
              <Button
                iconKey="check"
                label={saving ? 'Saving...' : 'Save Changes'}
                enabled={canSave}
                onClick={handleSave}
              />
            </div>
          </div>
        </ModalButtons>
      </div>
    </div>
  );
};

// Dev Tools
DatabaseSettingsAboutTab.displayName = 'DatabaseSettingsAboutTab';

export default DatabaseSettingsAboutTab;
